import React from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Grid, IconButton, Paper, Tooltip } from '@mui/material';
import CurrencyField from './CurrencyInput';
import DropdownField from './DropdownField';
import FormField from './FormField';
import Label from './Label';
import { TableCellTooltipSelect } from './TableCellTooltipSelect';
import { colors, formUi } from '../constants/globalcss';
import { ILabel } from '../constants/interfaces';
import { IFormStateValue } from '../utils/formUtils';
import {
  Row,
  URGENCY_OPTIONS,
  YES_NO_OPTIONS,
  WORKFLOW_TABLE_ROW_STRIPE,
  childRowGridItemSx,
  isOtherLineItem,
  rowFieldErrors,
  getLineItemNestedLabels,
  buildSelectFieldForRow,
} from './childRowUtils';

const workflowOutlinedIconButtonSx = {
  '&:hover': {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
    color: colors.background.paper,
  },
  backgroundColor: colors.background.paper,
  border: `1px solid ${colors.primary}`,
  borderRadius: 2,
  color: colors.primary,
  padding: '6px',
};

const childRowDeleteButtonSx = {
  ...workflowOutlinedIconButtonSx,
  position: 'absolute',
  right: 6,
  top: 6,
  zIndex: 1,
} as const;

interface ChildRowProps {
  row: Row;
  rowIndex: number;
  field: ILabel;
  rows: Row[];
  valueFieldTemplate: ILabel;
  formStateForVisibility?: Record<string, IFormStateValue>;
  onSelect: (rowId: string, newText: string) => void;
  onValueChange: (rowId: string, v: string) => void;
  onOtherDescriptionChange: (rowId: string, v: string) => void;
  onNeighborComplaintChange: (rowId: string, v: string) => void;
  onCodeIssueChange: (rowId: string, v: string) => void;
  onPreservationOrCosmeticChange: (rowId: string, v: string) => void;
  onEmergencyChange: (rowId: string, v: string) => void;
  onPicturesAttachedChange: (rowId: string, v: string) => void;
  onDelete: (rowId: string) => void;
}

export function ChildRow({
  row,
  rowIndex,
  field,
  rows,
  valueFieldTemplate,
  formStateForVisibility,
  onSelect,
  onValueChange,
  onOtherDescriptionChange,
  onNeighborComplaintChange,
  onCodeIssueChange,
  onPreservationOrCosmeticChange,
  onEmergencyChange,
  onPicturesAttachedChange,
  onDelete,
}: ChildRowProps) {
  const nestedLabels = getLineItemNestedLabels(field, row);
  const errs = rowFieldErrors(
    row,
    field.isMandatory,
    nestedLabels,
    formStateForVisibility
  );
  const rowDisabled = !row.selectedText;
  const lineItemField: ILabel = {
    ...buildSelectFieldForRow(field, row, rows),
    text: 'Line item',
  };

  return (
    <Paper
      elevation={1}
      sx={{
        backgroundColor:
          rowIndex % 2 === 0
            ? colors.background.paper
            : WORKFLOW_TABLE_ROW_STRIPE,
        border: formUi.card.border,
        borderRadius: formUi.card.radius,
        boxShadow: formUi.card.shadow,
        mb: 1,
        p: '10px',
        position: 'relative',
      }}
    >
      <Tooltip title="Remove this row">
        <IconButton
          aria-label="Remove this row"
          onClick={() => onDelete(row.id)}
          size="small"
          sx={childRowDeleteButtonSx}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Grid
        columnSpacing={{ md: 3, xs: 2 }}
        container
        rowSpacing={2}
        sx={{ pr: 4 }}
      >
        <Grid size={{ xs: 12, md: 6 }} sx={childRowGridItemSx}>
          <DropdownField
            field={lineItemField}
            value={row.selectedText}
            onChange={(v) => onSelect(row.id, v)}
            errorText={undefined}
            isLabelHidden={false}
            placeholder="Select line item"
            width="100%"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} sx={childRowGridItemSx}>
          <CurrencyField
            disabled={rowDisabled}
            displayOnly={false}
            errorText={errs.amount}
            field={valueFieldTemplate}
            isLabelHidden={false}
            onChange={(v) => onValueChange(row.id, v)}
            placeholder={row.selectedText ? undefined : '—'}
            value={row.response}
            width="100%"
          />
        </Grid>

        {isOtherLineItem(row.selectedText) ? (
          <Grid size={{ xs: 12, md: 6 }} sx={childRowGridItemSx}>
            <FormField
              value={row.otherDescription}
              errorText={errs.otherDescription}
              field={
                {
                  formula: null,
                  isMandatory: true,
                  labelId: field.labelId,
                  options: [],
                  orderIndex: 0,
                  rules: null,
                  text: 'If other, please describe',
                  type: 'text',
                } as ILabel
              }
              handleInputChange={(_, v) =>
                onOtherDescriptionChange(row.id, String(v))
              }
              isInGrid
              isLabelHidden={false}
              width="100%"
            />
          </Grid>
        ) : null}

        <Grid size={{ xs: 12, md: 6 }} sx={childRowGridItemSx}>
          <Label
            htmlFor={`${row.id}-neighbor-complaint`}
            required
            text="Neighbor complaint?"
          />
          <TableCellTooltipSelect
            disabled={rowDisabled}
            errorText={errs.neighborComplaint}
            id={`${row.id}-neighbor-complaint`}
            labelText="Neighbor complaint?"
            onChange={(v) => onNeighborComplaintChange(row.id, v)}
            options={YES_NO_OPTIONS}
            value={row.neighborComplaint}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} sx={childRowGridItemSx}>
          <Label htmlFor={`${row.id}-code-issue`} required text="Code Issue?" />
          <TableCellTooltipSelect
            disabled={rowDisabled}
            errorText={errs.codeIssue}
            id={`${row.id}-code-issue`}
            labelText="Code Issue?"
            onChange={(v) => onCodeIssueChange(row.id, v)}
            options={YES_NO_OPTIONS}
            value={row.codeIssue}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} sx={childRowGridItemSx}>
          <Label
            htmlFor={`${row.id}-urgency`}
            required
            text="Is this a preservation or cosmetic change?"
          />
          <TableCellTooltipSelect
            disabled={rowDisabled}
            errorText={errs.preservationOrCosmetic}
            id={`${row.id}-urgency`}
            labelText="Is this a preservation or cosmetic change?"
            onChange={(v) => onPreservationOrCosmeticChange(row.id, v)}
            options={URGENCY_OPTIONS}
            value={row.preservationOrCosmetic}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} sx={childRowGridItemSx}>
          <Label
            htmlFor={`${row.id}-emergency`}
            required
            text="Is this an emergency?"
          />
          <TableCellTooltipSelect
            disabled={rowDisabled}
            errorText={errs.isEmergency}
            id={`${row.id}-emergency`}
            labelText="Is this an emergency?"
            onChange={(v) => onEmergencyChange(row.id, v)}
            options={YES_NO_OPTIONS}
            value={row.isEmergency}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} sx={childRowGridItemSx}>
          <Label
            htmlFor={`${row.id}-pictures-attached`}
            required
            text="Will pictures of the issue be attached?"
          />
          <TableCellTooltipSelect
            disabled={rowDisabled}
            errorText={errs.willPicturesAttached}
            id={`${row.id}-pictures-attached`}
            labelText="Will pictures of the issue be attached?"
            onChange={(v) => onPicturesAttachedChange(row.id, v)}
            options={YES_NO_OPTIONS}
            value={row.willPicturesAttached}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}
